/**
 * Custom React hook for managing the command flow logic in a command-line interface application.
 * Handles user input, session management, output persistence, translations, and command execution.
 * Integrates with the CommandFlowEngine and provides state and handlers for UI components.
 *
 * @returns {Object} Hook state and handlers:
 *   @property {Array} output - The current output messages for the command flow.
 *   @property {string|null} currentPrompt - The current prompt awaiting user input.
 *   @property {Function} handleUserInput - Function to process user input and update state.
 *   @property {boolean} inSession - Indicates if a command session is active.
 *   @property {boolean} isLoading - Indicates if the engine is processing a command.
 */
import { useState, useEffect, useRef } from "react";
import CommandFlowEngine from "../../engine/CommandFlowEngine";
import { getTranslation } from "../../config/translations";
import { getInitialMessage } from "../../utils/command/commandLineUtils";
import {
  handleSessionInput,
  handleNewCommand,
} from "../../utils/command/inputHandlingUtils";
import { createSetters } from "../../utils/session/stateUtils";
import useCommandFlowStore from "../../store/commandFlowStore";
import {
  resolvePrompt,
  resolveOutput,
} from "../../utils/command/commandLineUtils";

export const useCommandFlow = () => {
  // Store values
  const commands = useCommandFlowStore((state) => state.commands);
  const initialMessage = useCommandFlowStore((state) => state.initialMessage);
  const translations = useCommandFlowStore((state) => state.translations);
  const incrementResetToken = useCommandFlowStore(
    (state) => state.incrementResetToken
  );
  const output = useCommandFlowStore((state) => state.output);
  const wasCleared = useCommandFlowStore((state) => state.wasCleared);
  const isLoading = useCommandFlowStore((state) => state.isLoading);
  const inSession = useCommandFlowStore((state) => state.inSession);
  const setIsLoading = useCommandFlowStore((state) => state.setIsLoading);
  const setInSession = useCommandFlowStore((state) => state.setInSession);

  // Helper function for translations
  const t = (key, replacements = {}) =>
    getTranslation(key, translations, replacements);

  const initMessage = getInitialMessage(initialMessage);

  // Initialize output if empty and not intentionally cleared.
  // Skip during frame mode — frames clear output between transitions
  // and the welcome message must not appear mid-lesson.
  useEffect(() => {
    if (output.length === 0 && !wasCleared) {
      const { frameMode, addOutputWithTypingEffect: setOutput } = useCommandFlowStore.getState();
      if (frameMode) return;
      const defaultWelcome = [{ type: "output", text: t("helpTip") }];
      const initialOutput =
        initialMessage && initialMessage.length > 0
          ? initialMessage.map((msg) =>
              typeof msg === "string" ? { type: "output", text: msg } : msg
            )
          : defaultWelcome;
      setOutput(initialOutput);
    }
  }, [initialMessage, t, output.length, wasCleared]);

  // Get currentPrompt from store instead of local state
  const currentPrompt = useCommandFlowStore((state) => state.currentPrompt);
  const setCurrentPrompt = useCommandFlowStore(
    (state) => state.setCurrentPrompt
  );

  const inSessionRef = useRef(inSession);
  const engineRef = useRef(
    new CommandFlowEngine(
      commands,
      translations,
      useCommandFlowStore.getState()
    )
  );
  const prevCommandsRef = useRef(commands);
  const prevTranslationsRef = useRef(translations);

  const engine = engineRef.current;

  // Update engine when commands or translations change, but preserve session state
  useEffect(() => {
    // Only recreate if commands or translations actually changed (by reference)
    const commandsChanged = commands !== prevCommandsRef.current;
    const translationsChanged = translations !== prevTranslationsRef.current;

    if (commandsChanged || translationsChanged) {
      const oldEngine = engineRef.current;
      const isInActiveSession = oldEngine?.isInSession();

      console.log(
        "🔄 Engine update triggered - commands changed:",
        commandsChanged,
        "translations changed:",
        translationsChanged,
        "in session:",
        isInActiveSession
      );

      if (isInActiveSession && !commandsChanged) {
        // Only prevent recreation if it's just translations changing and we're in session
        // Commands changing might be necessary for session to work properly
        console.log(
          "⚠️ Preventing engine recreation during active session - only translations changed"
        );
        prevTranslationsRef.current = translations;
        return;
      }

      if (isInActiveSession && commandsChanged) {
        console.log(
          "⚠️ Commands changed during session - this might cause issues, but proceeding"
        );
      }

      console.log("🔄 Recreating engine with new commands/translations");
      engineRef.current = new CommandFlowEngine(
        commands,
        translations,
        useCommandFlowStore.getState()
      );

      prevCommandsRef.current = commands;
      prevTranslationsRef.current = translations;
    }
  }, [commands, translations]);

  // Create setters object
  const setters = createSetters({
    setCurrentPrompt,
    incrementResetToken,
  });

  useEffect(() => {
    inSessionRef.current = inSession;
  }, [inSession]);

  // Synchronize inSession state with engine session state.
  // Skip while loading — the engine may be mid-redirect where session
  // state is transient (old session ended, new one not yet started).
  useEffect(() => {
    if (isLoading) return;
    const engineInSession = engine.isInSession();
    if (inSession !== engineInSession) {
      setInSession(engineInSession);
      // If engine is no longer in session, clear stale prompt so
      // PromptDisplay falls back to the default ">" label.
      if (!engineInSession) {
        setCurrentPrompt(null);
      }
    }
  }, [inSession, engine, isLoading]);

  const handleUserInput = async (
    input,
    onCommandComplete,
    onStepStart,
    onStepComplete,
    onCommandAbort,
    onCommandBack,
    onCommandStart
  ) => {
    const trimmed = input.trim();

    const commonParams = {
      trimmed,
      currentPrompt,
      engine,
      setters,
      resolvePrompt,
      resolveOutput,
      commands,
      onStepStart,
      onStepComplete,
      onCommandComplete,
      onCommandStart,
      onCommandAbort,
      onCommandBack,
      initMessage,
    };

    // Handle session vs new command
    let isInSession = engine.isInSession();
    const storeInSession = useCommandFlowStore.getState().inSession;
    console.log("🔍 Session State Check:", {
      input: trimmed,
      engineInSession: isInSession,
      storeInSession: storeInSession,
      engineSessionInfo: engine.sessionCoordinator?.getSessionInfo(),
    });

    // Defensive: if engine thinks it's in session but there is no valid
    // current step (e.g. after a redirect chain completed but the session
    // was not properly cleaned up), clean up the stale session and treat
    // the input as a new command.
    if (isInSession) {
      const sessionInfo = engine.sessionCoordinator?.getSessionInfo();
      if (!sessionInfo?.currentStep) {
        console.warn(
          "⚠️ Engine in session but no current step — cleaning up stale session"
        );
        engine.endSession();
        const storeState = useCommandFlowStore.getState();
        storeState.setInSession(false);
        storeState.setCurrentPrompt(null);
        isInSession = false;
      }
    }

    if (isInSession) {
      await handleSessionInput(commonParams);
    } else {
      await handleNewCommand(commonParams);
    }
  };

  return {
    output,
    currentPrompt,
    handleUserInput,
    engine, // Expose engine for accessing current step
  };
};
